// src/pages/historyData.js
import { 
  FaTrain, 
  FaFlag, 
  FaPaw, 
  FaPlane, 
  FaWater, 
  FaRocket, 
  FaMedal, 
  FaMountain, 
  FaLaptopCode, 
  FaCity
} from "react-icons/fa";

// Utility for conditional class names
export const cn = (...classes) => classes.filter(Boolean).join(" ");

// Milestone data with theme metadata
export const historyMilestones = [
  {
    year: "1890",
    title: "Coming of the Railroad",
    category: "Vintage",
    description: "The arrival of the Rio Grande Western railroad transformed Utah from a remote frontier into a connected hub of commerce.",
    fullStory: "In 1890, the completion of the Rio Grande Western railroad line through the Rio Grande valley and into Salt Lake City marked a turning point for Utah. This expansion connected the territory's rich mineral resources—silver, copper, and lead—to national markets, sparking an industrial boom. The railroad didn't just move goods; it moved people, ideas, and a new sense of belonging to the broader United States.",
    legacy: "Towns like Helper and Carbon County grew around these tracks, and the railroad remains a vital part of Utah's infrastructure today, carrying both heavy freight and the scenic California Zephyr through the state's breathtaking canyons.",
    icon: <FaTrain />,
    image: "/img/history/railroad_1890.webp",
    theme: "vintage",
    colors: { from: "from-amber-100", to: "to-amber-50", accent: "amber-600" }
  },
  {
    year: "1896",
    title: "The 45th State",
    category: "Vintage",
    description: "Utah was officially admitted to the Union, marking the culmination of a decades-long struggle for statehood.",
    fullStory: "After six failed attempts spanning nearly 50 years, Utah was finally admitted as the 45th state on January 4, 1896. President Grover Cleveland signed the proclamation after Utah's constitutional convention agreed to the separation of church and state. Heber M. Wells was elected as the first governor, leading a population that was eager to prove its loyalty and industry to the nation.",
    legacy: "Statehood day is celebrated annually on January 4th, reminding Utahns of the persistence and compromise that led to their entry into the American family.",
    icon: <FaFlag />,
    image: "/img/history/statehood_1896.webp",
    theme: "vintage",
    colors: { from: "from-amber-200", to: "to-orange-50", accent: "orange-600" }
  },
  {
    year: "1909",
    title: "Dinosaur National Monument",
    category: "Classical",
    description: "Earl Douglass discovered dinosaur bones in northeastern Utah, leading to the creation of one of the world's premier fossil sites.",
    fullStory: "In 1909, paleontologist Earl Douglass discovered a series of perfectly preserved dinosaur bones in the hills near Jensen, Utah. This 'dinosaur quarry' eventually became Dinosaur National Monument, established by President Woodrow Wilson in 1915. The site contains fossils from the Jurassic period, including Allosaurus, Stegosaurus, and Apatosaurus.",
    legacy: "The monument is now a world-renowned research site where visitors can see over 1,500 dinosaur bones still embedded in the cliff face, preserving a window into a pre-historic world.",
    icon: <FaPaw />,
    image: "/img/history/dinosaur_1909.webp",
    theme: "classic",
    colors: { from: "from-green-100", to: "to-emerald-50", accent: "emerald-800" }
  },
  {
    year: "1942",
    title: "Hill Air Force Base",
    category: "Mid-Century",
    description: "The opening of Hill AFB brought a massive military presence to Ogden, shaping the region's economy for decades.",
    fullStory: "As the United States ramped up its involvement in World War II, the Army Air Corps chose a site south of Ogden for a major logistics and maintenance hub. Named after test pilot Major Ployer Peter Hill, the base became a critical repair center for bombers and fighters. During the peak of the war, thousands of civilians, including 'Rosie the Riveter' figures, worked 24/7 to keep the Allied air forces flying.",
    legacy: "Hill AFB remains Utah's largest single-site employer and a global leader in aerospace maintenance, currently serving as a primary home for the F-35 Lightning II.",
    icon: <FaPlane />,
    image: "/img/history/hillafb_1942.webp",
    theme: "mid-century",
    colors: { from: "from-blue-100", to: "to-slate-200", accent: "blue-900" }
  },
  {
    year: "1962",
    title: "Great Salt Lake State Park",
    category: "Modernist",
    description: "Utah officially recognized the Great Salt Lake as a primary recreational and ecological asset.",
    fullStory: "In 1962, the establishment of the Great Salt Lake State Park signaled a new era of environmental awareness and tourism for Utah's most iconic natural feature. The lake, a remnant of the prehistoric Lake Bonneville, has unique salinity levels that allow people to float effortlessly. It serves as a vital stopover for millions of migratory birds on the Pacific Flyway.",
    legacy: "Today, the lake faces environmental challenges due to water diversion and drought, leading to a renewed statewide effort to preserve this 'inland sea' for future generations.",
    icon: <FaWater />,
    image: "/img/history/saltlake_1962.webp",
    theme: "modernist",
    colors: { from: "from-cyan-100", to: "to-sky-50", accent: "sky-700" }
  },
  {
    year: "1969",
    title: "Apollo Moon Mission",
    category: "Space Age",
    description: "Utah's aerospace industry and desert testing sites played a critical role in NASA's mission to land on the moon.",
    fullStory: "While Neil Armstrong walked on the moon in 1969, the engines that helped launch him and the equipment tested for the lunar surface had deep roots in Utah. The Thiokol Corporation (now Northrop Grumman) tested solid rocket motors in the Utah desert. Additionally, the unique lunar-like landscape of the Utah desert was used to test early moon rover prototypes.",
    legacy: "Utah continues to be a 'Space State,' with its companies building the boosters for NASA's Artemis missions, aimed at returning humans to the moon.",
    icon: <FaRocket />,
    image: "/img/history/apollo_1969.webp",
    theme: "space-age",
    colors: { from: "from-indigo-200", to: "to-slate-800", accent: "blue-600" }
  },
  {
    year: "1984",
    title: "Winter Olympics Expansion",
    category: "Modern",
    description: "Utah began its major push into winter sports, eventually leading to the successful 2002 Salt Lake City Winter Olympics.",
    fullStory: "In the mid-80s, Utah leaders made a strategic decision to invest in world-class winter sports infrastructure. The creation of the Utah Olympic Park in Park City and the renovation of venues across the Wasatch Front turned the state into a premier training ground for elite athletes. This foresight was rewarded when Salt Lake City was chosen to host the 2002 Winter Games.",
    legacy: "The spirit of the games lives on in Utah's 'Greatest Snow on Earth,' with the state frequently hosting World Cup events and serving as a prospective host for the 2034 Olympics.",
    icon: <FaMedal />,
    image: "/img/history/snow-boarding.webp",
    theme: "modern",
    colors: { from: "from-rose-100", to: "to-pink-50", accent: "rose-600" }
  },
  {
    year: "1995",
    title: "Five National Parks",
    category: "Modern",
    description: "Utah's 'Mighty 5'—Zion, Arches, Bryce Canyon, Capitol Reef, and Canyonlands—became a global tourism phenomenon.",
    fullStory: "By 1995, all five of Utah's unique geological wonderlands were fully established as National Parks. The 'Mighty 5' campaign later popularized these destinations, showcasing the dramatic contrast between red rock arches, towering hoodoos, and deep canyon systems. This period saw a massive shift in Utah's economy toward outdoor recreation and international tourism.",
    legacy: "Tourism is now a multi-billion dollar industry in Utah, with the national parks serving as the crown jewels of the state's natural beauty.",
    icon: <FaMountain />,
    image: "/img/history/arches.webp",
    theme: "modern",
    colors: { from: "from-orange-100", to: "to-red-50", accent: "orange-800" }
  },
  {
    year: "2010",
    title: "Silicon Slopes Boom",
    category: "Tech Era",
    description: "The rise of Adobe, Vivint, and Qualtrics transformed the Wasatch Front into a premier technology hub.",
    fullStory: "The term 'Silicon Slopes' was coined to describe the explosive growth of Utah's technology sector along the corridor between Salt Lake City and Provo. Companies like Ancestry, Domo, and Workfront joined established giants, drawn by Utah's high quality of life, young workforce, and business-friendly environment.",
    legacy: "Utah is now consistently ranked as one of the best states for tech jobs and entrepreneurship, proving that innovation can thrive in the shadow of the mountains.",
    icon: <FaLaptopCode />,
    image: "/img/history/siliconslopes_2010.webp",
    theme: "modern-tech",
    colors: { from: "from-blue-600", to: "to-indigo-800", accent: "blue-400" }
  },
  {
    year: "2024",
    title: "Utah Today",
    category: "Ultra Modern",
    description: "Utah stands as one of the fastest-growing and most economically diverse states in America, looking toward a sustainable future.",
    fullStory: "In 2024, Utah is a model of growth and economic resilience. The state is tackling challenges like housing affordability and water conservation while continuing to lead in aerospace, energy (Operation Gigawatt), and life sciences. With a population exceeding 3.4 million, Utah is more diverse and dynamic than ever.",
    legacy: "The Beehive State remains true to its motto of 'Industry,' building a future that balances rapid development with the preservation of its legendary natural wonders.",
    icon: <FaCity />,
    image: "/img/history/slc.webp",
    theme: "ultra-modern",
    colors: { from: "from-violet-500", to: "to-fuchsia-600", accent: "violet-600" }
  }
];
